import { Button } from '@round/whitelabel-ui';
import { Modal } from '@round/whitelabel-ui';
import styles from './CreateSongModal.module.css';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import SongForm, { SongFormValues, validationSchema } from '../../components/SongForm/SongForm';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import { isNumber, mapApiErrorsToFormikErrors } from 'utility/utility';
import { formatSongFormValuesToApiData } from '../../components/SongForm/helpers';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useCreateSongMutation, useInvalidateMusicSong } from '@round/creatorbase-helpers/api-hooks';

const createUtils = creatorbaseQueries.song.utils.create;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (song: creatorbase.Song) => void;
};

const CreateSongModal = ({ isOpen, onClose, onSuccess }: Props) => {
    const { selectedClientId } = useClientSelect();
    const { create } = useCreateSongMutation();
    const invalidateMusicSong = useInvalidateMusicSong();

    const handleSubmit = async (values: SongFormValues, helpers: FormikHelpers<SongFormValues>) => {
        if (!isNumber(selectedClientId)) {
            return;
        }

        try {
            const formattedValues = formatSongFormValuesToApiData(values);
            const song = await create({ ...formattedValues, client_id: selectedClientId });
            void invalidateMusicSong();
            showNotification('Song created', 'info');
            onSuccess?.(song);
            formik.resetForm();
            onClose();
        } catch (e) {
            if (!(e instanceof Error) || !createUtils.isExpectedErrorResponse(e) || e.response.status !== 400) {
                showNotification('Could not create song', 'error');
                return;
            }

            if (Array.isArray(e.response.data)) {
                showNotification(e.response.data.join(', '), 'error');
            } else {
                helpers.setErrors(mapApiErrorsToFormikErrors(e.response.data));
            }
        }
    };

    const formik = useFormik<SongFormValues>({
        initialValues: {
            title: '',
            artist_name: '',
            status: 'released',
            spotify_url: '',
            tiktok_audios: [],
            instagram_audios: [],
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: handleSubmit,
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
            <Modal.Header>
                <Modal.Title>Add a new song</Modal.Title>
            </Modal.Header>

            <FormikProvider value={formik}>
                <Modal.Body>
                    <SongForm />
                </Modal.Body>

                <Modal.Actions>
                    <Button
                        appearance="primary"
                        onClick={() => {
                            formik.handleSubmit();
                        }}
                        isLoading={formik.isSubmitting}
                        disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    >
                        Create song
                    </Button>
                    <Button appearance="ghost" onClick={handleClose}>
                        Cancel
                    </Button>
                </Modal.Actions>
            </FormikProvider>
        </Modal>
    );
};

export default CreateSongModal;
